import React from "react";
import { Box } from "@mui/material";
import { useApp, useTheme } from "contexts";

type RocketLaunchpadProps = {
  isLaunching: boolean;
  onLaunch: () => void;
};

export const RocketLaunchpad: React.FC<RocketLaunchpadProps> = ({
  isLaunching,
  onLaunch,
}) => {
  const { theme } = useTheme();
  const { t } = useApp();
  const isDark = theme.id === "dark";

  return (
    <Box
      onClick={onLaunch}
      role="button"
      tabIndex={0}
      aria-label={
        t.atmosphere?.click_to_launch || "Click to Ignite & Launch Rocket"
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onLaunch();
        }
      }}
      sx={{
        position: "relative",
        ml: "auto",
        mr: { xs: 1, sm: 2, md: 4 },
        width: { xs: 136, md: 168 },
        height: { xs: 186, md: 226 },
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        pointerEvents: "auto",
        cursor: isLaunching ? "default" : "pointer",
        "&:hover .launch-badge": {
          opacity: 1,
          transform: "translateX(-50%) translateY(-4px)",
          borderColor: theme.color.primary.color,
        },
      }}
    >
      {/* Hover / Active Ignition Callout Badge */}
      <Box
        className="launch-badge"
        sx={{
          position: "absolute",
          top: -28,
          left: "56%",
          transform: "translateX(-50%)",
          px: theme.spacing.xs,
          py: 0.35,
          backgroundColor: isLaunching
            ? theme.color.secondary.accent
            : theme.color.theme.color,
          color: isLaunching ? "#ffffff" : theme.color.primary.color,
          border: `1.5px solid ${
            isLaunching ? theme.color.font.color : theme.color.border.color
          }`,
          borderRadius: theme.border.square,
          boxShadow: `2px 2px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
          fontFamily: '"Space Mono", monospace',
          fontSize: 9,
          fontWeight: theme.font.bold,
          whiteSpace: "nowrap",
          letterSpacing: "0.08em",
          opacity: isLaunching ? 1 : 0.85,
          transition: "all 0.2s ease",
          zIndex: 20,
        }}
      >
        {isLaunching
          ? t.atmosphere?.ignition_active || "IGNITION // LIFTOFF!"
          : t.atmosphere?.click_to_launch || "CLICK TO IGNITE // LAUNCH"}
      </Box>

      {/* Ambient Cryo-Vapor Clouds Venting from Umbilical Tower */}
      {!isLaunching && (
        <>
          <Box
            sx={{
              position: "absolute",
              bottom: 70,
              left: "32%",
              width: 24,
              height: 12,
              backgroundColor: isDark
                ? "rgba(240, 240, 249, 0.25)"
                : "rgba(53, 89, 168, 0.18)",
              borderRadius: theme.border.square,
              animation: "cryo-vent 2.4s infinite ease-out",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 110,
              left: "30%",
              width: 20,
              height: 10,
              backgroundColor: isDark
                ? "rgba(240, 240, 249, 0.2)"
                : "rgba(53, 89, 168, 0.15)",
              borderRadius: theme.border.square,
              animation: "cryo-vent 2.4s infinite ease-out 1.1s",
            }}
          />
        </>
      )}

      {/* Pre-Ignition Idle Glow */}
      {!isLaunching && (
        <Box
          sx={{
            position: "absolute",
            bottom: 2,
            left: "54%",
            transform: "translateX(-50%)",
            width: 26,
            height: 16,
            background:
              "radial-gradient(circle, #fffdf0 10%, #ff9f43 55%, rgba(254, 156, 136, 0) 100%)",
            animation: "thruster-idle 0.6s infinite ease-in-out",
            filter: "blur(1px)",
          }}
        />
      )}

      {/* Billowing Smoke Clouds on Pad During Ignition */}
      {isLaunching && (
        <Box
          sx={{
            position: "absolute",
            bottom: 6,
            left: "56%",
            transform: "translateX(-50%)",
            width: 160,
            height: 60,
            pointerEvents: "none",
            zIndex: 15,
          }}
        >
          {/* Left Trench Plume Wave 1 */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: "35%",
              width: 34,
              height: 24,
              backgroundColor: isDark ? "#e2e4f0" : "#d5d8e8",
              border: `2px solid ${theme.color.font.color}`,
              borderRadius: theme.border.square,
              animation: "smoke-billow-left 2.2s ease-out forwards",
            }}
          />
          {/* Right Trench Plume Wave 1 */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: "45%",
              width: 34,
              height: 24,
              backgroundColor: isDark ? "#e2e4f0" : "#d5d8e8",
              border: `2px solid ${theme.color.font.color}`,
              borderRadius: theme.border.square,
              animation: "smoke-billow-right 2.2s ease-out forwards",
            }}
          />
          {/* Center Core Blast Cloud */}
          <Box
            sx={{
              position: "absolute",
              bottom: -4,
              left: "38%",
              width: 42,
              height: 28,
              backgroundColor: "#fff3b0",
              border: `2px solid ${theme.color.font.color}`,
              borderRadius: theme.border.square,
              animation: "smoke-billow-center 1.9s ease-out forwards",
            }}
          />
          {/* Left Trench Plume Wave 2 (Delayed) */}
          <Box
            sx={{
              position: "absolute",
              bottom: 4,
              left: "30%",
              width: 28,
              height: 20,
              backgroundColor: theme.color.theme.color,
              border: `1.5px solid ${theme.color.font.color}`,
              borderRadius: theme.border.square,
              animation: "smoke-billow-left 2.5s ease-out 0.35s forwards",
              opacity: 0,
            }}
          />
          {/* Right Trench Plume Wave 2 (Delayed) */}
          <Box
            sx={{
              position: "absolute",
              bottom: 4,
              left: "48%",
              width: 28,
              height: 20,
              backgroundColor: theme.color.theme.color,
              border: `1.5px solid ${theme.color.font.color}`,
              borderRadius: theme.border.square,
              animation: "smoke-billow-right 2.5s ease-out 0.35s forwards",
              opacity: 0,
            }}
          />
        </Box>
      )}

      {/* 16-Bit Tactile Technical SVG: Umbilical Gantry Tower + Rocket */}
      <Box
        component="svg"
        viewBox="0 0 160 220"
        sx={{
          width: "100%",
          height: "100%",
          overflow: "visible",
        }}
      >
        {/* Blast Deflector Trench Base */}
        <polygon
          points="15,220 25,204 145,204 155,220"
          fill={theme.color.theme.color}
          stroke={theme.color.border.color}
          strokeWidth="2"
        />
        <rect
          x="32"
          y="198"
          width="106"
          height="6"
          fill={theme.color.background.accent}
          stroke={theme.color.border.color}
          strokeWidth="1.5"
        />

        {/* Umbilical Launch Tower (Left Structure) */}
        <rect
          x="34"
          y="36"
          width="22"
          height="162"
          fill="none"
          stroke={theme.color.border.color}
          strokeWidth="2"
        />
        {/* Tower Cross-Bracing */}
        <path
          d="M34,36 L56,64 M56,36 L34,64 M34,64 L56,92 M56,64 L34,92 M34,92 L56,120 M56,92 L34,120 M34,120 L56,148 M56,120 L34,148 M34,148 L56,176 M56,148 L34,176 M34,176 L56,198 M56,176 L34,198"
          stroke={theme.color.border.color}
          strokeWidth="1.2"
          opacity="0.75"
        />
        {/* Tower Top Lightning Mast & Warning Beacon */}
        <line
          x1="45"
          y1="14"
          x2="45"
          y2="36"
          stroke={theme.color.border.color}
          strokeWidth="2"
        />
        <rect
          x="42"
          y="10"
          width="6"
          height="6"
          fill={theme.color.secondary.accent}
          style={{
            animation: isLaunching
              ? "beacon-blink 0.25s infinite"
              : "beacon-blink 1.2s infinite",
          }}
        />

        {/* Retractable Umbilical Swing Arms */}
        <rect
          x="56"
          y="68"
          width="24"
          height="5"
          fill={theme.color.theme.color}
          stroke={theme.color.border.color}
          strokeWidth="1.5"
          style={{
            transformOrigin: "56px 70px",
            animation: isLaunching
              ? "arm-retract-1 3.8s ease-out forwards"
              : "none",
          }}
        />
        <rect
          x="56"
          y="116"
          width="24"
          height="5"
          fill={theme.color.theme.color}
          stroke={theme.color.border.color}
          strokeWidth="1.5"
          style={{
            transformOrigin: "56px 118px",
            animation: isLaunching
              ? "arm-retract-2 3.8s ease-out forwards"
              : "none",
          }}
        />
        <rect
          x="56"
          y="162"
          width="22"
          height="5"
          fill={theme.color.theme.color}
          stroke={theme.color.border.color}
          strokeWidth="1.5"
          style={{
            transformOrigin: "56px 164px",
            animation: isLaunching
              ? "arm-retract-3 3.8s ease-out forwards"
              : "none",
          }}
        />

        {/* Ascending Orbital Rocket Vehicle + Afterburner Flame */}
        <g
          style={{
            animation: isLaunching
              ? "rocket-liftoff 3.8s cubic-bezier(0.45, 0, 0.85, 0.15) forwards"
              : "none",
          }}
        >
          {isLaunching && (
            <g
              style={{
                transformOrigin: "94px 198px",
                animation: "afterburner-pulse 0.14s infinite ease-in-out",
              }}
            >
              {/* Outer Supersonic Exhaust Cone */}
              <polygon
                points="75,198 94,285 113,198"
                fill="#ff6b35"
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              {/* Mid Thermal Core */}
              <polygon points="81,198 94,258 107,198" fill="#ffc038" />
              {/* White-Hot Mach Diamond Core */}
              <polygon points="87,198 94,232 101,198" fill="#fffdf0" />
            </g>
          )}

          {/* Side Solid Rocket Boosters (SRBs) */}
          <path
            d="M72,118 L76,106 L80,118 L80,192 L72,192 Z"
            fill={theme.color.theme.color}
            stroke={theme.color.font.color}
            strokeWidth="1.5"
          />
          <path
            d="M108,118 L112,106 L116,118 L116,192 L108,192 Z"
            fill={theme.color.theme.color}
            stroke={theme.color.font.color}
            strokeWidth="1.5"
          />

          {/* Main Core Stage Body */}
          <rect
            x="80"
            y="66"
            width="28"
            height="126"
            fill={theme.color.background.accent}
            stroke={theme.color.font.color}
            strokeWidth="2"
          />

          {/* Stage Separation Rings & Retro Roll Pattern */}
          <rect
            x="80"
            y="98"
            width="28"
            height="8"
            fill={theme.color.primary.color}
          />
          <rect
            x="80"
            y="148"
            width="28"
            height="6"
            fill={theme.color.font.color}
          />

          {/* Vertical Telemetry Stripe on Core */}
          <line
            x1="94"
            y1="72"
            x2="94"
            y2="188"
            stroke={theme.color.border.color}
            strokeWidth="1.5"
            strokeDasharray="4 4"
          />

          {/* Payload Fairing / Nosecone */}
          <path
            d="M80,66 C80,42 94,24 94,24 C94,24 108,42 108,66 Z"
            fill={theme.color.background.accent}
            stroke={theme.color.font.color}
            strokeWidth="2"
          />
          {/* Nosecone Tip Accent */}
          <path
            d="M89,36 C91,29 94,24 94,24 C94,24 97,29 99,36 Z"
            fill={theme.color.secondary.accent}
          />

          {/* Crew Capsule Porthole Window */}
          <circle
            cx="94"
            cy="52"
            r="4.5"
            fill={theme.color.primary.color}
            stroke={theme.color.font.color}
            strokeWidth="1.5"
          />

          {/* Aerodynamic Grid Fins / Base Stabilizers */}
          <polygon
            points="72,174 64,192 72,192"
            fill={theme.color.primary.color}
            stroke={theme.color.font.color}
            strokeWidth="1.5"
          />
          <polygon
            points="116,174 124,192 116,192"
            fill={theme.color.primary.color}
            stroke={theme.color.font.color}
            strokeWidth="1.5"
          />

          {/* Engine Nozzles */}
          <polygon
            points="83,192 80,198 90,198 87,192"
            fill={theme.color.font.color}
          />
          <polygon
            points="93,192 90,198 98,198 95,192"
            fill={theme.color.font.color}
          />
          <polygon
            points="101,192 98,198 108,198 105,192"
            fill={theme.color.font.color}
          />
        </g>
      </Box>
    </Box>
  );
};

